import type { Bbox, Candidate, Station } from '../../consumer/fire-signals/contract';
import {
  longitudeSpan,
  projectPoint,
  signalHref,
  STATUS_LABELS,
  wrapLongitude,
} from '../../consumer/fire-signals/contract';
export function GeographicMap({
  candidates,
  stations,
  bbox,
  search,
  areas,
}: {
  candidates: Candidate[];
  stations: Station[];
  bbox: Bbox;
  search: string;
  areas: { areaRef: string; name: string; bbox: Bbox }[];
}) {
  return (
    <div className="fs-map">
      <div
        className="fs-map-canvas"
        tabIndex={0}
        role="region"
        aria-label="地理地图，可用方向键水平滚动，Tab 进入候选"
      >
        <svg
          viewBox="0 0 1000 480"
          role="group"
          aria-label="EPSG:4326 卫星像元与 NOAA 气象站地理坐标地图"
        >
          <title>经纬度地理地图 · 像元中心并非火势范围</title>
          <rect x="60" y="30" width="880" height="400" fill="#f0f6fb" />
          {[0, 1, 2, 3, 4].map((i) => {
            const x = 60 + i * 220,
              y = 30 + i * 100;
            return (
              <g key={i}>
                <path d={`M${x} 30V430 M60 ${y}H940`} stroke="#ccdae7" strokeDasharray="3 6" />
                <text x={x} y="451" textAnchor="middle">
                  {wrapLongitude(bbox[0] + (longitudeSpan(bbox) * i) / 4).toFixed(2)}°
                </text>
                <text x="52" y={y + 5} textAnchor="end">
                  {(bbox[3] - ((bbox[3] - bbox[1]) * i) / 4).toFixed(2)}°
                </text>
              </g>
            );
          })}
          <text x="500" y="476" textAnchor="middle">
            经度 Longitude · EPSG:4326
          </text>
          <text x="12" y="240" transform="rotate(-90 12 240)" textAnchor="middle">
            纬度 Latitude
          </text>
          {areas.map((area) => {
            const a = projectPoint(area.bbox[0], area.bbox[3], bbox);
            const b = projectPoint(area.bbox[2], area.bbox[1], bbox);
            return a && b && b[0] >= a[0] ? (
              <g key={area.areaRef}>
                <rect
                  x={a[0]}
                  y={a[1]}
                  width={b[0] - a[0]}
                  height={b[1] - a[1]}
                  fill="none"
                  stroke="#708ba9"
                  strokeDasharray="8 6"
                />
                <title>{`${area.name} · 预冻结研究范围 ${area.bbox.join(',')}`}</title>
              </g>
            ) : null;
          })}
          {candidates.map((c) => {
            const p = projectPoint(c.satellite.longitude, c.satellite.latitude, bbox);
            return p ? (
              <a
                key={c.candidateId}
                href={signalHref(search, c.candidateId)}
                data-candidate-id={c.candidateId}
                data-latitude={c.satellite.latitude}
                data-longitude={c.satellite.longitude}
                aria-label={`${STATUS_LABELS[c.association.status]} · ${c.satellite.acquiredAtUTC} · ${c.satellite.latitude}, ${c.satellite.longitude}`}
              >
                <circle
                  cx={p[0]}
                  cy={p[1]}
                  r="7"
                  className={
                    c.association.status === 'WEATHER_CONTEXT_LINKED' ? 'fs-linked' : 'fs-unlinked'
                  }
                />
                <title>{`${c.satellite.acquiredAtUTC} · ${c.satellite.latitude}, ${c.satellite.longitude}`}</title>
              </a>
            ) : null;
          })}
          {stations.map((s) => {
            const p = projectPoint(s.longitude, s.latitude, bbox);
            return p ? (
              <g key={s.stationId} data-station-id={s.stationId}>
                <path
                  d={`M${p[0]} ${p[1] - 9}l9 18h-18z`}
                  fill="#183b66"
                  stroke="white"
                  strokeWidth="2"
                />
                <text
                  x={Math.min(870, p[0] + 14)}
                  y={Math.max(45, p[1] - 10)}
                  className="fs-station-label"
                >
                  {s.name}
                </text>
                <title>{`NOAA ${s.stationId} · ${s.latitude}, ${s.longitude}`}</title>
              </g>
            ) : null;
          })}
        </svg>
      </div>
      <div className="fs-legend">
        <span>
          <i className="fs-dot fs-linked" />
          已关联天气背景
        </span>
        <span>
          <i className="fs-dot fs-unlinked" />
          未关联 / 资料不足
        </span>
        <span>▲ NOAA 实际气象站</span>
      </div>
      <p className="fs-caption">
        NASA FIRMS / NOAA NCEI ·
        经纬度格网，无第三方底图。显示当前分页的真实像元中心，不表示燃烧范围；下方列表提供键盘访问。
      </p>
    </div>
  );
}
